/**
 * stroke-engine.js — распознавание инсульта: логика, время и цифры.
 *
 * Pure logic, zero DOM. Грузится /stroke-test и tests/js/stroke-engine.test.mjs.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ НЕ ПРОСТО «FAST», КАК У ВСЕХ
 * ═══════════════════════════════════════════════════════════════════════════
 * Практически весь сегмент — и памятки, и приложения — учит схеме FAST: лицо,
 * рука, речь, время. Проблема измерена и опубликована: **39,1% инсультов
 * задней циркуляции FAST пропускает** — речь о доле среди инсультов ЗАДНЕЙ
 * циркуляции, а не среди всех. Чувствительность BE-FAST против FAST на этой
 * группе — 97,8% против 58,7% (одно ретроспективное исследование, Сидней,
 * 2024: вызовы инсульт-команды, то есть уже подозреваемые пациенты). Разница
 * — две буквы: Balance (внезапная потеря равновесия) и Eyes (внезапное
 * нарушение зрения). Ими и отличается инструмент, который учит правильному,
 * от инструмента, который повторяет плакат.
 *
 * У BE-FAST есть обратная сторона, и её надо назвать честно: специфичность
 * ниже (10% против 39,8%), то есть ложных тревог больше. Для ПОЛЬЗОВАТЕЛЯ это
 * правильный размен, и вот почему: цена ложного срабатывания — поездка скорой,
 * цена пропуска — необратимая инвалидность или смерть. Асимметрия такая, что
 * инструмент обязан быть смещён в сторону звонка, и он смещён.
 *
 * ── Время ────────────────────────────────────────────────────────────────
 * Главный вопрос диспетчера и врача — НЕ «какие симптомы», а «когда человека
 * последний раз видели нормальным». Именно от этой отметки считаются окна
 * тромболизиса (до 4,5 ч, в отдельных случаях до 9 ч по данным визуализации) и
 * тромбэктомии (до 24 ч при подходящей картине). Люди этот момент не помнят и
 * называют «ну, где-то час назад». Поэтому инструмент спрашивает про него
 * ПЕРВЫМ, ставит метку и потом показывает её обратным отсчётом.
 *
 * ⚠️ Окна — это ориентиры для разговора, а не критерии допуска: решение
 * принимает врач по визуализации. Инструмент не имеет права ни обнадёживать
 * («ещё три часа есть»), ни отговаривать («поздно»). Обе интонации убивают.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) { module.exports = api; }
    if (root) { root.StrokeEngine = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
'use strict';

var MIN = 60000, HOUR = 3600000;

/* ═══════════════════════════════════════════════════════════════════════
   ПРИЗНАКИ
   ═══════════════════════════════════════════════════════════════════════
   core: входит в исходный FAST · posterior: то, что FAST пропускает.
   Каждый признак — отдельный шаг, и любой ОДИН положительный уже даёт
   вызов. Складывать баллы здесь нельзя: односторонняя слабость руки не
   «половина инсульта».
   ═══════════════════════════════════════════════════════════════════════ */
var SIGNS = [
    { id: 'balance', group: 'posterior', measured: false },
    { id: 'eyes',    group: 'posterior', measured: false },
    { id: 'face',    group: 'core',      measured: 'camera' },
    { id: 'arms',    group: 'core',      measured: 'motion' },
    { id: 'speech',  group: 'core',      measured: 'audio' }
];

/* Дополнительные внезапные симптомы, которые не входят ни в FAST, ни в
   BE-FAST, но описаны как самостоятельный повод вызвать скорую: внезапная
   сильнейшая головная боль (классическая картина субарахноидального
   кровоизлияния) и внезапное онемение половины тела. */
var EXTRA = ['thunderclap', 'numbness', 'confusion'];

/* ═══════════════════════════════════════════════════════════════════════
   ТЕЛЕФОНЫ ЭКСТРЕННЫХ СЛУЖБ
   ═══════════════════════════════════════════════════════════════════════
   Список намеренно КОРОТКИЙ и с явной оговоркой в интерфейсе: ошибиться тут
   хуже, чем не знать. 112 работает во всём ЕС, в России, Украине и с любого
   мобильного во многих других странах — поэтому он всегда показан вторым.
*/
var EMERGENCY = {
    RU: { primary: '103', alt: '112' },
    UA: { primary: '103', alt: '112' },
    BY: { primary: '103', alt: '112' },
    KZ: { primary: '103', alt: '112' },
    US: { primary: '911', alt: null },
    CA: { primary: '911', alt: null },
    GB: { primary: '999', alt: '112' },
    DE: { primary: '112', alt: null },
    FR: { primary: '15',  alt: '112' },
    ES: { primary: '112', alt: null },
    PT: { primary: '112', alt: null },
    BR: { primary: '192', alt: '193' },
    IT: { primary: '112', alt: null },
    PL: { primary: '112', alt: '999' },
    KR: { primary: '119', alt: null },
    JP: { primary: '119', alt: null },
    ID: { primary: '119', alt: '112' },
    IN: { primary: '108', alt: '112' },
    AE: { primary: '998', alt: '999' },
    SA: { primary: '997', alt: '911' },
    EG: { primary: '123', alt: null },
    TR: { primary: '112', alt: null },
    AU: { primary: '000', alt: '112' },
    NZ: { primary: '111', alt: '112' },
    /* Дополнено 2026-09-13: страна теперь берётся по GeoIP, а не по языку, и
       англоязычный посетитель из Великобритании, Индии или ОАЭ больше не видит
       911. В таблицу внесены только номера, в которых нет двусмысленности; всё
       остальное получает 112, который работает с любого мобильного в ЕС и
       в большинстве сетей мира. */
    NL: { primary: '112', alt: null }, BE: { primary: '112', alt: null },
    AT: { primary: '144', alt: '112' }, CH: { primary: '144', alt: '112' },
    SE: { primary: '112', alt: null }, NO: { primary: '113', alt: '112' },
    FI: { primary: '112', alt: null }, DK: { primary: '112', alt: null },
    IE: { primary: '112', alt: '999' }, CZ: { primary: '155', alt: '112' },
    SK: { primary: '155', alt: '112' }, HU: { primary: '104', alt: '112' },
    RO: { primary: '112', alt: null }, BG: { primary: '112', alt: null },
    GR: { primary: '166', alt: '112' }, LT: { primary: '112', alt: null },
    LV: { primary: '113', alt: '112' }, EE: { primary: '112', alt: null },
    GE: { primary: '112', alt: null }, AM: { primary: '103', alt: '112' },
    AZ: { primary: '103', alt: '112' }, UZ: { primary: '103', alt: '112' },
    KG: { primary: '103', alt: '112' }, TJ: { primary: '103', alt: '112' },
    MD: { primary: '112', alt: '903' }, IL: { primary: '101', alt: '112' },
    MX: { primary: '911', alt: null }, AR: { primary: '107', alt: '911' },
    CL: { primary: '131', alt: null }, CO: { primary: '123', alt: null },
    CN: { primary: '120', alt: null }, HK: { primary: '999', alt: '112' },
    TW: { primary: '119', alt: '112' }, SG: { primary: '995', alt: null },
    MY: { primary: '999', alt: '112' }, TH: { primary: '1669', alt: null },
    VN: { primary: '115', alt: null }, PH: { primary: '911', alt: null },
    BD: { primary: '999', alt: null }, QA: { primary: '999', alt: null },
    KW: { primary: '112', alt: null }, BH: { primary: '999', alt: null },
    OM: { primary: '9999', alt: null }, JO: { primary: '911', alt: null },
    ZA: { primary: '10177', alt: '112' }, NG: { primary: '112', alt: null },
    KE: { primary: '999', alt: '112' }, TN: { primary: '190', alt: null },
    DZ: { primary: '14', alt: null }
};
var DEFAULT_EMERGENCY = { primary: '112', alt: null };

function emergencyFor(cc) {
    return EMERGENCY[(cc || '').toUpperCase()] || DEFAULT_EMERGENCY;
}

/* ═══════════════════════════════════════════════════════════════════════
   ИЗМЕРЕНИЕ РУК
   ═══════════════════════════════════════════════════════════════════════
   Классическая проба — вытянуть обе руки и удержать 10 секунд: при
   односторонней слабости рука уходит вниз и внутрь. Телефон в руке даёт эту
   пробу измеримой: вектор силы тяжести в системе координат устройства
   поворачивается ровно на угол наклона руки.
*/

/** Угол между двумя векторами гравитации, в градусах. */
function tiltAngle(a, b) {
    var dot = a.x * b.x + a.y * b.y + a.z * b.z;
    var na = Math.hypot(a.x, a.y, a.z), nb = Math.hypot(b.x, b.y, b.z);
    if (!na || !nb) return 0;
    var c = Math.max(-1, Math.min(1, dot / (na * nb)));
    return Math.acos(c) * 180 / Math.PI;
}

/**
 * @param samples [{t, x, y, z}] — ускорение с гравитацией, ~30–60 Гц
 * @returns {driftDeg, dropped, seconds, valid, reason}
 *
 * `dropped` — падение больше 45°, то есть рука ушла резко: это уже не дрейф.
 * `valid` = false, когда данных мало или человек махал телефоном (шум выше
 * полезного сигнала). Врать в обе стороны здесь одинаково плохо, поэтому
 * непригодная запись честно объявляется непригодной.
 */
function armDrift(samples) {
    if (!samples || samples.length < 20) {
        return { valid: false, reason: 'too_few', driftDeg: 0, dropped: false, seconds: 0 };
    }
    var n = samples.length;
    var seconds = (samples[n - 1].t - samples[0].t) / 1000;
    if (seconds < 4) return { valid: false, reason: 'too_short', driftDeg: 0, dropped: false, seconds: seconds };

    var head = samples.slice(0, Math.max(3, Math.floor(n * 0.15)));
    var tail = samples.slice(Math.floor(n * 0.85));
    var mean = function (arr) {
        var s = { x: 0, y: 0, z: 0 };
        arr.forEach(function (p) { s.x += p.x; s.y += p.y; s.z += p.z; });
        return { x: s.x / arr.length, y: s.y / arr.length, z: s.z / arr.length };
    };
    var a = mean(head), b = mean(tail);
    var drift = tiltAngle(a, b);

    /* Шум: средний разброс соседних отсчётов. Если рука трясётся сильнее, чем
       уходит, измерение бессмысленно. */
    var jitter = 0;
    for (var i = 1; i < n; i++) jitter += tiltAngle(samples[i - 1], samples[i]);
    jitter /= (n - 1);

    var maxAngle = 0;
    for (var j = 0; j < n; j++) maxAngle = Math.max(maxAngle, tiltAngle(a, samples[j]));

    if (jitter > 6) return { valid: false, reason: 'shaky', driftDeg: drift, dropped: false, seconds: seconds, jitter: jitter };

    return {
        valid: true, reason: null,
        driftDeg: Math.round(drift * 10) / 10,
        maxDeg: Math.round(maxAngle * 10) / 10,
        dropped: maxAngle > 45,
        jitter: Math.round(jitter * 100) / 100,
        seconds: Math.round(seconds * 10) / 10
    };
}

/**
 * Сравнение двух рук. Инсульт даёт ОДНОСТОРОННЮЮ слабость, поэтому
 * значим не сам дрейф (он есть у всех), а РАЗНИЦА между руками.
 * Порог 12° — консервативный: лучше лишний вызов, чем пропуск.
 */
function armVerdict(left, right) {
    if (!left || !right || !left.valid || !right.valid) {
        return { positive: null, reason: 'invalid' };
    }
    var diff = Math.abs(left.driftDeg - right.driftDeg);
    var weaker = left.driftDeg > right.driftDeg ? 'left' : 'right';
    return {
        positive: diff >= 12 || left.dropped || right.dropped,
        asymmetryDeg: Math.round(diff * 10) / 10,
        weaker: weaker,
        dropped: left.dropped || right.dropped,
        reason: null
    };
}

/* ═══════════════════════════════════════════════════════════════════════
   ВРЕМЕННЫЕ ОКНА
   ═══════════════════════════════════════════════════════════════════════ */

var WINDOWS = [
    { id: 'thrombolysis', hours: 4.5 },
    { id: 'extended',     hours: 9 },
    { id: 'thrombectomy', hours: 24 }
];

/**
 * @param lastWellTs метка «последний раз видели нормальным»
 * @param nowTs
 * @returns {elapsedMin, windows:[{id, hours, leftMin, open}], unknown}
 */
function timeWindows(lastWellTs, nowTs, opts) {
    var now = nowTs || Date.now();
    /* atLeast: время известно только как «не меньше» (кнопка «4 часа и
       больше»). Тогда остаток окна — это ВЕРХНЯЯ граница, и отсчёт обязан
       это говорить: «осталось 30 мин» при «прошло четыре часа или сутки»
       было бы выдумкой. До 2026-09-13 так и было. */
    var atLeast = !!(opts && opts.atLeast);
    if (!lastWellTs) {
        return { unknown: true, elapsedMin: null, windows: WINDOWS.map(function (w) {
            return { id: w.id, hours: w.hours, leftMin: null, open: null };
        }) };
    }
    var elapsed = Math.max(0, now - lastWellTs);
    return {
        unknown: false,
        atLeast: atLeast,
        elapsedMin: Math.round(elapsed / MIN),
        windows: WINDOWS.map(function (w) {
            var left = Math.round((w.hours * HOUR - elapsed) / MIN);
            return { id: w.id, hours: w.hours, leftMin: left, open: left > 0, upTo: atLeast && left > 0 };
        })
    };
}

/* ═══════════════════════════════════════════════════════════════════════
   ВЕРДИКТ
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * @param answers {face:true|false|null, arms:…, speech:…, balance:…, eyes:…,
 *                 thunderclap:…, numbness:…, confusion:…}
 * @param opts {lastWellTs, nowTs, sudden:boolean}
 *
 * ⚠️ Баллы НЕ складываются. Один положительный признак — это вызов. Такое
 * правило кажется грубым, но оно и есть клиническое: FAST и BE-FAST устроены
 * как «любой из», а не «двое из трёх». Сумма баллов здесь была бы наукообразной
 * формой отговаривания.
 */
function assess(answers, opts) {
    opts = opts || {};
    var a = answers || {};
    var positives = [];
    var unknowns = [];

    SIGNS.forEach(function (s) {
        if (a[s.id] === true) positives.push(s.id);
        else if (a[s.id] == null) unknowns.push(s.id);
    });
    EXTRA.forEach(function (id) {
        if (a[id] === true) positives.push(id);
    });

    var posteriorOnly = positives.length > 0 && positives.every(function (id) {
        return id === 'balance' || id === 'eyes';
    });

    var verdict;
    if (positives.length > 0) verdict = 'call';
    else if (unknowns.length >= 3) verdict = 'incomplete';
    else if (opts.sudden) verdict = 'call_sudden';
    else verdict = 'negative';

    return {
        verdict: verdict,
        positives: positives,
        unknowns: unknowns,
        /* Отдельный флаг ради того самого, ради чего написан весь инструмент:
           когда сработали ТОЛЬКО равновесие и зрение, обычная схема FAST не
           увидела бы ничего, и человек остался бы дома. */
        posteriorOnly: posteriorOnly,
        time: timeWindows(opts.lastWellTs, opts.nowTs, { atLeast: opts.atLeast }),
        /* Список того, чего делать НЕЛЬЗЯ, — часть результата, а не сноска.
           Аспирин при геморрагическом инсульте усиливает кровотечение, а
           отличить его без томографии невозможно. */
        donts: ['no_aspirin', 'no_food', 'no_drive', 'no_sleep', 'no_wait'],
        /* Пока едет скорая: то, что реально делают руки, а не «сохраняйте
           спокойствие». */
        waiting: ['position', 'vomit', 'stay', 'note', 'meds', 'door']
    };
}

/** Что продиктовать диспетчеру: порядок именно тот, что он спросит. */
function dispatchScript(state) {
    return {
        order: ['address', 'what', 'when', 'who', 'meds', 'access'],
        lastWellIso: state && state.lastWellTs ? new Date(state.lastWellTs).toISOString() : null,
        positives: state && state.positives ? state.positives : []
    };
}

return {
    SIGNS: SIGNS, EXTRA: EXTRA, WINDOWS: WINDOWS,
    EMERGENCY: EMERGENCY, emergencyFor: emergencyFor,
    tiltAngle: tiltAngle, armDrift: armDrift, armVerdict: armVerdict,
    timeWindows: timeWindows, assess: assess, dispatchScript: dispatchScript
};
});
